import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { BookOpen, Check, Code2, Gift, GraduationCap, Mail, PlayCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import PaymentsNote from '../components/PaymentsNote';
import { LoadingState } from '../components/ui';
import { billingService } from '../services/api';
import { siteUrl } from '../utils/site';

// The app's front page for visitors who aren't logged in (WEB-12): what Webots Academy is, how it works, the plans as
// they are sold (PLAN-1..4, the same facts as the website's pricing) and the legal links (WEB-10). Paddle's domain review
// refused app.webots.academy while it showed only a login wall (the checkout has since moved to the website, PLAN-17,
// but the app's domain is still listed with Paddle as the product). Logged-in users go straight to their
// dashboard. Light on purpose: only the shared UI pieces and a few icons, nothing of the editor or the viewer.

const STEPS = [
  { icon: <BookOpen />, title: 'Pick a lesson', text: 'Each one is a robotics challenge with its theory: line following, PID control, odometry, vision, manipulation.' },
  { icon: <Code2 />, title: 'Write the controller', text: 'Edit the robot’s Python controller right in the browser. Nothing to install.' },
  { icon: <PlayCircle />, title: 'Run it in Webots', text: 'Your code drives the robot in a real Webots simulation; see the result, improve it, move on.' },
];

interface Plan {
  audience: string;
  name: string;
  price: string;
  period?: string;
  summary: string;
  features: React.ReactNode[];
  cta: { label: string; to: string };
  highlight?: boolean;
}

const PLANS: Plan[] = [
  {
    audience: 'For learners',
    name: 'Free',
    price: '€0',
    summary: 'Try any lesson.',
    features: ['All public lessons and theory', 'Saved code and progress', <><strong>10 minutes</strong> of simulation a day</>],
    cta: { label: 'Start free', to: '/signup' },
  },
  {
    audience: 'For learners',
    name: 'Student',
    price: '€9',
    period: '/ month',
    summary: 'For yourself. Cancel anytime.',
    features: ['Everything in Free', <><strong>1 hour</strong> of simulation a day</>, 'Every lesson and partner course'],
    cta: { label: 'Get the Student plan', to: '/signup?plan=student' },
    highlight: true,
  },
  {
    audience: 'For educators',
    name: 'Evaluation class',
    price: '€0',
    summary: 'Try the whole teaching workflow, free and without a card.',
    features: ['One class with up to 3 students', 'Assignments, submissions, reviews', 'Your own lessons from GitHub'],
    cta: { label: 'Start free', to: '/signup?role=professor' },
  },
  {
    audience: 'For educators',
    name: 'Class',
    price: '€890',
    period: '/ semester',
    summary: 'For your whole class. Students pay nothing.',
    features: [<>Up to <strong>30 students</strong></>, <><strong>1 hour of simulation a day</strong> for each student, for the class&rsquo;s lessons</>, 'Progress, results and CSV export'],
    cta: { label: 'Get the Class plan', to: '/signup?role=professor' },
  },
];

const FrontPage: React.FC = () => {
  const { user, loading, connectionError } = useAuth();
  // PLAN-18: while online payment is pending (GET /api/billing/status, public), say so and point to the free month. Shown
  // only once the API says payments are off, so it disappears by itself when Paddle is configured.
  const [paymentsPending, setPaymentsPending] = useState(false);
  const visitor = !loading && !user && !connectionError;
  useEffect(() => {
    if (!visitor) return;
    let alive = true;
    billingService.status().then(({ payments }) => { if (alive) setPaymentsPending(!payments); }).catch(() => undefined);
    return () => { alive = false; };
  }, [visitor]);

  if (loading) {
    return <LoadingState className="min-h-screen" />;
  }
  // A session (or one the server couldn't check yet: the dashboard offers Retry) goes to the app itself.
  if (user || connectionError) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors duration-200">
      <nav className="bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between gap-3">
          <a href={siteUrl('/')} className="flex items-center gap-2 sm:gap-2.5 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm">
              <GraduationCap className="w-5 h-5" />
            </span>
            <span className="hidden min-[360px]:block text-[15px] sm:text-base font-semibold text-gray-900 dark:text-white whitespace-nowrap">Webots Academy</span>
          </a>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <ThemeToggle className="!w-9 sm:!w-10" />
            <Link to="/login" className="btn btn-ghost px-2.5 sm:px-4">Log in</Link>
            <Link to="/signup" className="btn btn-primary px-3 sm:px-4">Sign up</Link>
          </div>
        </div>
      </nav>

      <main>
        <section className="relative overflow-hidden">
          {/* A soft glow behind the hero, as on the login page (AuthShell); decoration only. */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 h-[28rem] w-[56rem] max-w-[200vw] -translate-x-1/2 rounded-full bg-gradient-to-b from-blue-200/50 to-transparent blur-3xl dark:from-blue-950/40" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-14 pb-12 sm:pt-20 sm:pb-16 text-center">
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">Learn robotics in your browser</h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              Webots Academy runs Webots, the open-source robot simulator, for you: write a Python robot controller and run it in a real
              simulation, with nothing to install. Educators run whole classes: assign lessons, invite students with a link and review
              their submissions.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link to="/signup" className="btn btn-primary btn-lg">Sign up free</Link>
              <Link to="/login" className="btn btn-secondary btn-lg">Log in</Link>
            </div>
            <p className="mt-5 text-sm text-gray-500 dark:text-gray-400">
              <a href={siteUrl('/lessons')} className="link">Browse the lessons</a>
              {' · '}
              <a href={siteUrl('/')} className="link">About Webots Academy</a>
            </p>
          </div>
        </section>

        <section aria-labelledby="how-heading" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
          <h2 id="how-heading" className="text-2xl font-bold tracking-tight text-center text-gray-900 dark:text-white">How it works</h2>
          <ol className="mt-8 grid gap-4 sm:grid-cols-3">
            {STEPS.map((step, index) => (
              <li key={step.title} className="card p-5 sm:p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 [&_svg]:w-5 [&_svg]:h-5">
                    {step.icon}
                  </span>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{index + 1}. {step.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="plans-heading" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
          <h2 id="plans-heading" className="text-2xl font-bold tracking-tight text-center text-gray-900 dark:text-white">Plans</h2>
          <p className="mt-2 text-center text-gray-600 dark:text-gray-300">The lessons are free. You pay for simulation time.</p>
          {paymentsPending && (
            <p className="mx-auto mt-4 flex max-w-2xl items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-900/70 bg-blue-50 dark:bg-blue-950/40 px-4 py-3 text-sm text-blue-900 dark:text-blue-100" data-testid="payments-pending">
              <Gift className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Online payment is coming soon. <Link to="/signup" className="font-semibold underline underline-offset-2">Sign up</Link>, then email <a href="mailto:contact@webots.academy?subject=Free%20month" className="font-semibold underline underline-offset-2">contact@webots.academy</a> for a free month of the Student plan, or of the Class plan for your course.</span>
            </p>
          )}
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`card flex flex-col p-5 sm:p-6 ${plan.highlight ? 'ring-2 ring-blue-600 dark:ring-blue-500 border-transparent dark:border-transparent' : ''}`}
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">{plan.audience}</p>
                <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {plan.price}
                  {plan.period && <span className="ml-1 text-base font-medium text-gray-500 dark:text-gray-400">{plan.period}</span>}
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{plan.summary}</p>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to={plan.cta.to} className={`btn ${plan.highlight ? 'btn-primary' : 'btn-secondary'} mt-6 w-full`}>{plan.cta.label}</Link>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Prices in euros; taxes are added at checkout where they apply. Several professors or a whole department?{' '}
            <a href="mailto:contact@webots.academy" className="link">Talk to us</a>.
          </p>
        </section>
      </main>

      <footer className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-colors duration-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <nav aria-label="Legal and contact" className="flex flex-wrap gap-x-5 gap-y-2">
            <a href={siteUrl('/')} className="hover:underline">webots.academy</a>
            <a href={siteUrl('/lessons')} className="hover:underline">Lessons</a>
            <a href={siteUrl('/terms')} className="hover:underline">Terms of Service</a>
            <a href={siteUrl('/privacy')} className="hover:underline">Privacy Policy</a>
            <a href={siteUrl('/refunds')} className="hover:underline">Refund Policy</a>
            <a href="mailto:contact@webots.academy" className="inline-flex items-center gap-1.5 hover:underline">
              <Mail className="h-4 w-4" aria-hidden="true" />
              contact@webots.academy
            </a>
          </nav>
          <p>Webots Academy is operated by SPES ROBOTICS DOO NOVI SAD, Novi Sad, Serbia.</p>
          <PaymentsNote className="text-sm text-gray-600 dark:text-gray-400" />
        </div>
      </footer>
    </div>
  );
};

export default FrontPage;
